import React, { useEffect, useRef } from 'react'
import useAuthStore from '../stores/authStore'
import { GOOGLE_CLIENT_ID } from '../utils/constants'

const GoogleOAuth = ({ onSuccess, onError, className = '' }) => {
  const googleButtonRef = useRef(null)
  const { googleLogin, isLoading } = useAuthStore()

  useEffect(() => {
    // Only initialize if Google Client ID is configured
    if (!GOOGLE_CLIENT_ID) {
      return
    }

    // Check if Google Identity Services script is loaded
    if (typeof window.google === 'undefined') {
      // Load Google Identity Services script dynamically
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initializeGoogleAuth
      document.head.appendChild(script)
    } else {
      initializeGoogleAuth()
    }

    function initializeGoogleAuth() {
      if (window.google && window.google.accounts) {
        // Initialize Google Identity Services
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        // Render the sign-in button
        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 320,
          })
        }
      }
    }

    async function handleCredentialResponse(response) {
      try {
        const result = await googleLogin(response.credential)
        if (result.success) {
          onSuccess?.(result)
        } else {
          onError?.(result.error)
        }
      } catch (error) {
        onError?.(error.message || 'Google authentication failed')
      }
    }

    // Cleanup function
    return () => {
      // Remove Google button if component unmounts
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = ''
      }
    }
  }, [onSuccess, onError, googleLogin])

  // If Google Client ID is not configured, show configuration message
  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className={`text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg ${className}`}>
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Google OAuth is not configured. Please set <code>VITE_GOOGLE_CLIENT_ID</code> in your environment variables.
        </p>
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
          Get your client ID from the <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a>
        </p>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={googleButtonRef} 
        id="google-signin-button"
        className="w-full flex justify-center"
        style={{
          minWidth: '320px',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      />
      {isLoading && (
        <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 flex items-center justify-center rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Signing in...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoogleOAuth